import { groupBy, sum, sumBy } from "lodash";

import { ALL_CHANNELS_KEY } from "@/pages/scenario-builder/v3/api/constants";

import { HistoricalSpendDTO } from "../../../api/getHistoricalSpend";
import { bcpLayerKey } from "../../../utils/bcp";
import { SpendInput } from "../types";

export type HistoricalSpends = {
  maxTotal: number;
  total: number;
  nodes: Map<string, Record<string, ChannelSpend>>;
};

export type ChannelSpend = {
  channel: string;
  spend: number;
  max: number;
};

export const getHistoricalSpends = (
  spendRangesDTO: HistoricalSpendDTO[],
  scenarioTarget: string
): HistoricalSpends => {
  const targetFilteredSpends = scenarioTarget
    ? spendRangesDTO.filter(
        (dto) =>
          dto.is_node_channel_active &&
          dto.marketing_spend.marketing_spend_node.brand === scenarioTarget
      )
    : spendRangesDTO;

  const totalSpend = sumBy(targetFilteredSpends, (dto) => dto.marketing_spend.spend);
  const maxTotal = totalSpend * 2;

  const spendRanges = {
    total: totalSpend,
    maxTotal,
    nodes: new Map<string, Record<string, ChannelSpend>>(),
  };

  spendRangesDTO.forEach((spendRange) => {
    if (!spendRange.is_node_channel_active) {
      return;
    }

    const { channel, spend, marketing_spend_node } = spendRange.marketing_spend;

    const key = bcpLayerKey(marketing_spend_node);

    // Round final values for UI display, not intermediate calculations
    const channelSpendEntry = {
      channel,
      spend: roundDownToNearestThousand(spend),
      max: roundDownToNearestThousand(spend * 2),
    };

    if (spendRanges.nodes.has(key)) {
      const currentSpend = spendRanges.nodes.get(key)!;

      spendRanges.nodes.set(key, {
        ...currentSpend,
        [channel]: channelSpendEntry,
      });
    } else {
      spendRanges.nodes.set(key, {
        [channel]: channelSpendEntry,
      });
    }
  });

  return spendRanges;
};

export const roundDownToNearestThousand = (num: number): number => {
  return Math.floor(num / 1000) * 1000;
};

// Utility function to filter historical spends consistently across frontend validation
export const filterHistoricalSpendsForValidation = (
  historicalSpends: HistoricalSpendDTO[],
  filters: {
    brand?: string | null;
    category?: string | null;
    product?: string | null;
    channel?: string | null;
    geography?: string | null;
    isAllChannels?: boolean;
  }
): HistoricalSpendDTO[] => {
  return historicalSpends.filter((spend) => {
    const node = spend.marketing_spend.marketing_spend_node;

    if (filters.brand === "default") {
      return true;
    }

    return (
      (filters.brand === undefined || node.brand === filters.brand) &&
      (filters.category === undefined || node.category === filters.category) &&
      (filters.product === undefined || node.product === filters.product) &&
      (filters.channel === undefined ||
        filters.isAllChannels ||
        spend.marketing_spend.channel === filters.channel) &&
      (filters.geography === undefined || node.geography === filters.geography)
    );
  });
};

export type AllocatedSpend = {
  channel: string;
  spend: number;
  minSpend: number;
};

// We want to check where channel is "all", and set per-channel spends based on historical distribution
// If there are combinations of "all" and specific channels, the specific channels are ignored
export const preprocessSpendInputs = (
  spendInputs: SpendInput[],
  historicalSpends: HistoricalSpendDTO[]
): SpendInput[] => {
  // Group spend inputs by their BCP combination (brand, category, product, geography)
  const spendInputsByBcp = groupBy(
    spendInputs,
    (input) => `${input.brand}-${input.category}-${input.product}-${input.geography ?? null}`
  );

  const processedSpendInputs: SpendInput[] = [];

  // Process each BCP combination
  Object.values(spendInputsByBcp).forEach((bcpSpendInputs) => {
    // Check if there's an "all" channel in this BCP group
    const allChannelInput = bcpSpendInputs.find((input) => input.channel === ALL_CHANNELS_KEY);

    if (!allChannelInput) {
      const specificChannelInputs = bcpSpendInputs.filter(
        (input) => input.channel !== ALL_CHANNELS_KEY
      );
      // No "all" channel, pass through all specific channel inputs
      processedSpendInputs.push(...specificChannelInputs);
      return;
    }

    // There is an "all" channel, distribute its spend proportionally
    const allChannelSpend = allChannelInput.spend;
    const { brand, category, product, geography } = allChannelInput;

    // Get historical spends for this BCP combination, only active channels
    const relevantHistoricalSpends = historicalSpends.filter(
      (historical) =>
        historical.is_node_channel_active &&
        historical.marketing_spend.marketing_spend_node.brand === brand &&
        historical.marketing_spend.marketing_spend_node.category === category &&
        historical.marketing_spend.marketing_spend_node.product === product &&
        historical.marketing_spend.marketing_spend_node.geography === geography
    );

    // Calculate total historical spend for this BCP combination
    const totalHistoricalSpend = sumBy(
      relevantHistoricalSpends,
      (historical) => historical.marketing_spend.spend
    );

    // Distribute the "all" channel spend proportionally based on historical distribution
    const distributedSpends = relevantHistoricalSpends.map((historical) => {
      const historicalSpend = historical.marketing_spend.spend;
      const proportion = totalHistoricalSpend > 0 ? historicalSpend / totalHistoricalSpend : 0;
      const distributedSpend = allChannelSpend * proportion;

      return {
        channel: historical.marketing_spend.channel,
        brand,
        category,
        product,
        geography,
        spend: Math.floor(distributedSpend), // Round only the final result
      };
    });

    // Add the distributed spends to the result
    processedSpendInputs.push(...distributedSpends);
  });

  return processedSpendInputs;
};

export const allocateForecastBudget = (
  historicalSpends: HistoricalSpendDTO[],
  totalBudget: number,
  mediaConstraints: SpendInput[]
): SpendInput[] | undefined => {
  const preprocessedSpendInputs = preprocessSpendInputs(mediaConstraints, historicalSpends);

  const allocatedBudget = sum(preprocessedSpendInputs.map(({ spend }) => spend));
  if (allocatedBudget > totalBudget) {
    return;
  }

  const unallocatedHistoricalSpends = historicalSpends
    .filter(({ is_node_channel_active }) => is_node_channel_active)
    .filter(
      ({ marketing_spend }) =>
        !preprocessedSpendInputs.some(
          ({ brand, category, product, geography, channel }) =>
            marketing_spend.marketing_spend_node.brand === brand &&
            marketing_spend.marketing_spend_node.category === category &&
            marketing_spend.marketing_spend_node.product === product &&
            marketing_spend.marketing_spend_node.geography === geography &&
            marketing_spend.channel === channel
        )
    );
  const unallocatedHistoricalSpend = sum(
    unallocatedHistoricalSpends.map(({ marketing_spend }) => marketing_spend.spend)
  );

  const remainingBudget = totalBudget - allocatedBudget;
  let runningTotal = 0;
  let nonZeroHistoricalSpendIndex = -1;

  const scaledUnallocatedSpends = unallocatedHistoricalSpends.map<SpendInput>(
    ({ marketing_spend }, index) => {
      const scaledSpend =
        unallocatedHistoricalSpend > 0
          ? (marketing_spend.spend / unallocatedHistoricalSpend) * remainingBudget
          : 0;

      const flooredSpend = Math.floor(scaledSpend);
      runningTotal += flooredSpend;

      // Note the first index where we have non-zero historical spend
      if (nonZeroHistoricalSpendIndex === -1 && marketing_spend.spend > 0) {
        nonZeroHistoricalSpendIndex = index;
      }

      return {
        channel: marketing_spend.channel,
        brand: marketing_spend.marketing_spend_node.brand,
        category: marketing_spend.marketing_spend_node.category,
        product: marketing_spend.marketing_spend_node.product,
        geography: marketing_spend.marketing_spend_node.geography,
        spend: flooredSpend,
      };
    }
  );

  // Add the difference to ensure total budget equals sum of all spends
  const difference = remainingBudget - runningTotal;
  if (difference > 0 && nonZeroHistoricalSpendIndex >= 0) {
    const targetSpend = scaledUnallocatedSpends[nonZeroHistoricalSpendIndex];
    if (targetSpend) {
      targetSpend.spend += difference;
    }
  }

  const fullAllocation = [...preprocessedSpendInputs, ...scaledUnallocatedSpends];
  return fullAllocation;
};
